import type { FC, FormEvent, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { PublicPageProps } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useI18n } from '@/i18n'
import { localePath } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { SiteLayout } from '@/components/site/site-layout'
import { AuthShell } from '@/components/site/auth-shell'
import { CountrySelect } from '@/components/forms/country-select'
import { apiRequest, errorCode, errorFields } from '@/lib/client/api'
import { loadPublicProps, localeOf } from '@/lib/server/ssr'

import {
    Checkbox,
    Field,
    Input,
    PasswordInput
} from '@/components/ui/field'

interface RegisterForm {
    firstName: string
    lastName: string
    email: string
    phone: string
    nationality: string
    password: string
    passwordConfirm: string
    privacy: boolean
    marketing: boolean
}

const RegisterPage: FC<PublicPageProps> = (base): ReactNode => {
    const router = useRouter()
    const { t, locale } = useI18n()
    const [form, setForm] = useState<RegisterForm>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        nationality: '',
        password: '',
        passwordConfirm: '',
        privacy: false,
        marketing: false
    })
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)

    const update = <K extends keyof RegisterForm>(key: K, value: RegisterForm[K]) => {
        setForm(current => ({ ...current, [key]: value }))
        setErrors(current => ({ ...current, [key]: '' }))
    }

    const errorText = (key: string): string | null => {
        const code = errors[key]

        return code ? (t.errors as Record<string, string>)[code] ?? t.errors.validation : null
    }

    const validate = (): Record<string, string> => {
        const result: Record<string, string> = {}

        if (!form.firstName.trim()) result.firstName = 'required'
        if (!form.lastName.trim()) result.lastName = 'required'
        if (!form.email.trim()) result.email = 'required'
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) result.email = 'invalid_email'
        if (!form.phone.trim()) result.phone = 'required'
        else if (!/^[+()\d][\d\s()./-]{5,28}$/.test(form.phone.trim())) result.phone = 'invalid_phone'
        if (!form.nationality) result.nationality = 'invalid_country'
        if (form.password.length < 8) result.password = 'password_short'
        if (form.passwordConfirm !== form.password) result.passwordConfirm = 'password_mismatch'
        if (!form.privacy) result.privacy = 'consent_required'

        return result
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFormError(null)

        const found = validate()

        setErrors(found)

        if (Object.keys(found).length) {
            document.getElementById(`register-${Object.keys(found)[0]}`)?.focus()

            return
        }

        setLoading(true)

        try {
            const result = await apiRequest<{ email: string, emailSent: boolean }>('/api/auth/register', {
                method: 'POST',
                body: { ...form, privacyAccepted: form.privacy, locale }
            })

            await router.push({ pathname: '/verify-email', query: { email: result.email, sent: result.emailSent ? '1' : '0' } })
        }
        catch (caught) {
            const fields = errorFields(caught)

            if (Object.keys(fields).length) setErrors(fields)

            setFormError((t.errors as Record<string, string>)[errorCode(caught)] ?? t.errors.server_error)
            setLoading(false)
        }
    }

    return (
        <SiteLayout {...base}>
            <Seo title={t.meta.registerTitle} />

            <AuthShell
                title={t.auth.registerTitle}
                text={t.auth.registerText}
                footer={(
                    <p>
                        {t.auth.haveAccount}{' '}
                        <Link href='/login' className='font-semibold text-navy underline underline-offset-2'>{t.auth.loginLink}</Link>
                    </p>
                )}
            >
                <form onSubmit={submit} noValidate className='grid gap-5 sm:grid-cols-2'>
                    <Field label={t.auth.firstName} htmlFor='register-firstName' error={errorText('firstName')}>
                        <Input id='register-firstName' autoComplete='given-name' maxLength={80} value={form.firstName} invalid={Boolean(errors.firstName)} onChange={event => update('firstName', event.target.value)} />
                    </Field>

                    <Field label={t.auth.lastName} htmlFor='register-lastName' error={errorText('lastName')}>
                        <Input id='register-lastName' autoComplete='family-name' maxLength={80} value={form.lastName} invalid={Boolean(errors.lastName)} onChange={event => update('lastName', event.target.value)} />
                    </Field>

                    <Field label={t.auth.email} htmlFor='register-email' error={errorText('email')} className='sm:col-span-2'>
                        <Input id='register-email' type='email' autoComplete='email' maxLength={254} value={form.email} invalid={Boolean(errors.email)} onChange={event => update('email', event.target.value)} />
                    </Field>

                    <Field label={t.auth.phone} htmlFor='register-phone' error={errorText('phone')}>
                        <Input id='register-phone' type='tel' autoComplete='tel' dir='ltr' maxLength={30} value={form.phone} invalid={Boolean(errors.phone)} onChange={event => update('phone', event.target.value)} />
                    </Field>

                    <Field label={t.auth.nationality} htmlFor='register-nationality' error={errorText('nationality')}>
                        <CountrySelect
                            id='register-nationality'
                            value={form.nationality}
                            onChange={value => update('nationality', value)}
                            locale={locale}
                            placeholder={t.common.choose}
                            commonLabel={t.common.commonCountries}
                            allLabel={t.common.allCountries}
                            invalid={Boolean(errors.nationality)}
                        />
                    </Field>

                    <Field label={t.auth.password} htmlFor='register-password' error={errorText('password')} hint={t.auth.passwordHint}>
                        <PasswordInput
                            id='register-password'
                            autoComplete='new-password'
                            value={form.password}
                            invalid={Boolean(errors.password)}
                            onChange={event => update('password', event.target.value)}
                            showLabel={t.auth.showPassword}
                            hideLabel={t.auth.hidePassword}
                        />
                    </Field>

                    <Field label={t.auth.passwordConfirm} htmlFor='register-passwordConfirm' error={errorText('passwordConfirm')}>
                        <PasswordInput
                            id='register-passwordConfirm'
                            autoComplete='new-password'
                            value={form.passwordConfirm}
                            invalid={Boolean(errors.passwordConfirm)}
                            onChange={event => update('passwordConfirm', event.target.value)}
                            showLabel={t.auth.showPassword}
                            hideLabel={t.auth.hidePassword}
                        />
                    </Field>

                    <div className='flex flex-col gap-3 sm:col-span-2'>
                        <Checkbox
                            id='register-privacy'
                            checked={form.privacy}
                            invalid={Boolean(errors.privacy)}
                            onChange={event => update('privacy', event.target.checked)}
                            label={(
                                <>
                                    {t.auth.privacyBefore}
                                    <Link href='/privacy' target='_blank' className='font-semibold text-navy underline underline-offset-2'>{t.auth.privacyLink}</Link>
                                    {t.auth.privacyAfter}
                                </>
                            )}
                        />
                        {errors.privacy && <p role='alert' className='text-xs font-medium text-destructive'>{errorText('privacy')}</p>}

                        <Checkbox
                            id='register-marketing'
                            checked={form.marketing}
                            onChange={event => update('marketing', event.target.checked)}
                            label={<>{t.auth.marketing} <span className='text-muted-foreground'>({t.common.optional})</span></>}
                        />
                    </div>

                    {formError && (
                        <p role='alert' className='rounded-2xl bg-danger-soft px-4 py-3 text-sm font-medium text-destructive sm:col-span-2'>{formError}</p>
                    )}

                    <Button type='submit' size='lg' loading={loading} className='w-full sm:col-span-2'>
                        {loading ? t.auth.registering : t.auth.registerButton}
                    </Button>
                </form>
            </AuthShell>
        </SiteLayout>
    )
}

export const getServerSideProps: GetServerSideProps<PublicPageProps> = async (ctx) => {
    const base = await loadPublicProps(ctx)

    if (base.viewer) return { redirect: { destination: localePath(localeOf(ctx), '/account'), permanent: false } }

    return { props: base }
}

export default RegisterPage
